import React from "react";
import { Github, Youtube, MessageCircle, Linkedin, Music2, MapPin, GraduationCap, Briefcase, ExternalLink, CheckCircle2 } from "lucide-react";
import { Panel, Overline } from "../common/Panel";

const FEATURES = [
  "3-phase energy metering (PZEM-004T ×3)",
  "Real-time temperature monitoring (DS18B20)",
  "Air quality monitoring — MQ-135 (CO₂/NH₃/smoke detection)",
  "Remote device control over MQTT",
  "PIR motion-activated enclosure light",
  "AI energy assistant (conversational)",
  "AI voice call mode",
  "AI video call with frame analysis",
  "Multi-provider TTS engine",
  "Slash command control shortcuts",
  "Grafana / ThingSpeak / Google Sheets integration",
  "TTGO T-Camera snapshots",
  "Cloudflare Pages deployment",
];

const STACK = ["ESP32", "PZEM-004T ×3", "DS18B20", "MQ-135", "HiveMQ Cloud", "Gemini 2.5 Flash", "TTGO T-Camera", "Cloudflare Pages", "CPUT 2026"];

const SOCIALS = [
  { icon: Github, label: "GitHub", value: "henockhnk092-dot", href: "https://github.com/henockhnk092-dot" },
  { icon: Youtube, label: "YouTube", value: "@HNK2005", href: "https://youtube.com/@HNK2005" },
  { icon: Music2, label: "TikTok", value: "@codingfever", href: "https://tiktok.com/@codingfever" },
  { icon: MessageCircle, label: "WhatsApp", value: "+27 75 453 1797", href: "https://wa.me/27754531797" },
  { icon: Linkedin, label: "LinkedIn", value: "Henock Mukonkole", href: "https://linkedin.com" },
];

export default function About() {
  return (
    <div className="space-y-6">
      <div>
        <Overline>Project Information</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">About the System</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="p-6 lg:col-span-2 space-y-5">
          <p className="text-sm leading-relaxed text-[var(--t2)]">
            <span className="text-[var(--t1)] font-medium">Campus Energy Monitor</span> is a production-grade IoT platform that meters 3-phase electrical
            energy across a university campus. ESP32 microcontrollers paired with PZEM-004T sensors stream live voltage, current, power, power factor and
            energy to HiveMQ Cloud over MQTT. A DS18B20 temperature probe drives automatic fan control, while an MQ-135 gas sensor monitors air quality
            with GOOD/POOR/DANGER alerts. A TTGO T-Camera adds visual monitoring, and an on-board AI assistant lets operators query readings and issue
            control commands in natural language. Data is visualized in Grafana Cloud and this dashboard, deployed on Cloudflare Pages.
          </p>
          <div>
            <Overline>Feature List</Overline>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm" data-testid="about-feature">
                  <CheckCircle2 size={15} className="text-[var(--accent)] mt-0.5 shrink-0" /><span className="text-[var(--t2)]">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Overline>Tech Stack</Overline>
            <div className="flex flex-wrap gap-2 mt-3">
              {STACK.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full text-xs font-mono bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t1)]" data-testid="about-stack-badge">{s}</span>
              ))}
            </div>
          </div>
        </Panel>

        {/* Developer profile */}
        <Panel className="p-6" data-testid="developer-profile">
          <div className="flex flex-col items-center text-center">
            <img
              src="https://parkade-stitch-site.pages.dev/assets/images/henock-portrait.png"
              alt="Henock HNK"
              data-testid="developer-photo"
              className="w-28 h-28 rounded-full object-cover border-2 border-[var(--accent)] shadow-lg"
              onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1762290965691-e74072600c03?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"; }}
            />
            <h3 className="font-heading font-bold text-xl mt-4">Henock HNK</h3>
            <p className="text-[var(--accent)] font-mono text-sm">Computer Engineer</p>
            <p className="text-xs text-[var(--t2)] mt-1">CPUT Final Year 2026</p>

            <div className="w-full mt-4 space-y-2 text-xs text-[var(--t2)] font-mono">
              <div className="flex items-center justify-center gap-2"><GraduationCap size={13} /> Supervisor: M. Ratshitanga</div>
              <div className="flex items-center justify-center gap-2"><Briefcase size={13} /> Student No: 219383693</div>
              <div className="flex items-center justify-center gap-2"><MapPin size={13} /> Cape Town, South Africa</div>
            </div>

            <div className="flex gap-2 mt-5 w-full">
              <a href="https://parkade-stitch-site.pages.dev" target="_blank" rel="noreferrer" data-testid="about-portfolio" className="flex-1 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium flex items-center justify-center gap-1.5">Portfolio <ExternalLink size={13} /></a>
              <a href="https://wa.me/27754531797" target="_blank" rel="noreferrer" data-testid="about-book" className="flex-1 py-2 rounded-md border border-[var(--bd)] text-sm font-medium flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors">Book Services</a>
            </div>

            <div className="w-full mt-4 space-y-1.5">
              {SOCIALS.map(({ icon: Icon, label, value, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={`social-${label.toLowerCase()}`} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[var(--accent-soft)] transition-colors text-sm">
                  <Icon size={15} className="text-[var(--accent)] shrink-0" />
                  <span className="text-[var(--t2)]">{label}</span>
                  <span className="ml-auto font-mono text-xs truncate">{value}</span>
                </a>
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
